import { LIFECYCLE_IDLE } from '/bb/constants.js';
import { createWelford, serializeWelford, deserializeWelford } from '/bb/aggregators/welford.js';
import { createTrajectory, serializeTrajectory, deserializeTrajectory } from '/bb/aggregators/trajectory.js';
import { clampUnitInterval } from '/bb/normalizers.js';

/**
 * Single mutable in-memory state object. All listeners read/write through these
 * getters; nothing else holds long-lived references to the object.
 */

let state = createEmpty();

export function getState() {
  return state;
}

export function resetState() {
  state = createEmpty();
}

export function replaceState(next) {
  state = next;
}

function createPointerBucket() {
  return {
    lastT: 0,
    lastX: 0,
    lastY: 0,
    lastV: 0,
    sumDist: 0,
    sumTime: 0,
    sampleCount: 0,
    speed: createWelford(),
    accel: createWelford(),
    rightClicks: 0,
    clicks: { total: 0, onInteractive: 0 },
    latency: createWelford(),
    accuracyPx: createWelford(),
    accuracyNorm: createWelford(),
    pathDeviation: createWelford(),
    pressure: createWelford(),
    width: createWelford(),
    scroll: { count: 0, sumDist: 0, sumTime: 0, speed: createWelford(), depthMax: 0 },
    trajectory: createTrajectory(),
  };
}

function createSessionAccumulators() {
  return {
    priorSessionCount: 0,
    activeDaysCount: 0,
    pageFocusCount: 0,
    orientationChangeCount: 0,
    lastFieldChangeAt: 0,
    pauseBeforeSubmitMs: null,
    pointer: createPointerBucket(),
    touch: createPointerBucket(),
  };
}

function createEmpty() {
  return {
    lifecycle: LIFECYCLE_IDLE,
    startedAt: 0,
    endedAt: 0,
    /** Cumulative pause duration in ms (sum of paused intervals). */
    pausedTotalMs: 0,
    /** Timestamp of the last bb:pause; 0 when not currently paused. */
    pausedAt: 0,
    session: createSessionAccumulators(),
    /** Map<pageKey, PageAgg>. */
    pages: new Map(),
    currentPageKey: null,
    currentPageEnteredAt: 0,
    /** Lifetime visits hydrated from localStorage. Map<pageKey, { visit_count, last_visit_at }>. */
    pagesLifetime: new Map(),
    /** Map<"<pageKey>::<sectionId>", SectionAgg>. */
    sections: new Map(),
    /** Map<"<pageKey>::<formId>", FormAgg>. */
    forms: new Map(),
    /** Map<sectionId, { count, lastAt }> in lifetime localStorage (currently unused at session level). */
    /** Per-field state keyed "<pageKey>::<formId>::<fieldName>". */
    fields: new Map(),
    /** Track recent (lookback ~200ms) low-level pointer/keyboard events to attribute focusins. */
    transient: {
      lastKey: { wasTab: false, t: 0 },
      lastPointerDown: { target: null, t: 0, button: 0, x: 0, y: 0 },
      pendingAutofillProbes: [], // [{ elementRef, atMs }]
    },
  };
}

/** Page record. */
export function createPageAgg() {
  return {
    duration_ms: 0,
    visit_count: 0,
    visit_count_lifetime: 0,
    lastVisitMarkAt: 0,
  };
}

/** Section record (scoped to a page). */
export function createSectionAgg() {
  return {
    pageKey: null,
    sectionId: null,
    section_categories: [],
    text_length: 0,
    visits_count: 0,
    visits: [],
    reading_time_ms: 0,
    pause_count: 0,
    time_total_ms: 0,
    scroll: { count: 0, sumDist: 0, sumTime: 0, speed: createWelford(), depthMax: 0 },
    zoom_count: 0,
    selection_count: 0,
    /** Runtime: entered_at, last_activity_at, currently_visible, pause_started_at. */
    runtime: {
      enteredAt: 0,
      currentlyVisible: false,
      lastActivityAt: 0,
      pauseStartedAt: 0,
      lastSelectionAt: 0,
    },
  };
}

/** Form record (scoped to a page). */
export function createFormAgg() {
  return {
    pageKey: null,
    formId: null,
    is_submitted: false,
    submission_time_ms: null,
    time_on_form_ms: 0,
    active_time_on_form_ms: 0,
    visit_count_session: 0,
    visit_count_lifetime: 0,
    visits_before_submission_count: null,
    corrections: {
      early_count: 0,
      late_count: 0,
      total_count: 0,
      late_ratio: null,
    },
    runtime: {
      // First time this form was visibly rendered for the user. Unlike
      // firstFocusAt, this is set even when the user submits without touching
      // a field.
      firstVisibleAt: 0,
      firstFocusAt: 0,
      lastFocusOutAt: 0,
      activeSince: 0,
      currentlyFocused: false,
      visitsSeen: 0,
      lastVisitMarkAt: 0,
      lastInputFieldName: null,
    },
    fields: new Map(),
  };
}

/** Field record. */
export function createFieldAgg(name, type) {
  const base = {
    name,
    type,
    field_categories: [],
    runtime: {
      firstFocusAt: 0,
      lastFocusOutAt: 0,
      lastFocusInAt: 0,
      focusSessions: 0,
      currentlyFocused: false,
      firstInputAt: 0,
      lastInputAt: 0,
      hoverSince: 0,
      hoverAccumMs: 0,
      inputsThisFocus: 0,
      pasteThisFocus: false,
      lastInputValueLength: -1,
      lastKeyAt: 0,
      lastTimingKeyUpAt: 0,
      autofillProbeArmed: false,
      backspaceBurstLen: 0,
      backspaceBurstStarted: false,
      autocompleteAcceptedRecorded: false,
      autocompleteDetectedThisFocus: false,
      valueAtFocus: null,
      changedAwayFromFocusValue: false,
      returnedToOriginalRecordedThisFocus: false,
      keystrokesHeld: new Map(),
      rangeInputSeenSinceChange: false,
      rangeGestureActive: false,
      rangeGestureCounted: false,
      rangeGestureChangeSeen: false,
      rangeGestureEndedAt: 0,
      rangeGestureCommittedValue: null,
      genericInputSeenSinceChange: false,
      fileInputSeenSinceChange: false,
      filePickerOpenedAt: 0,
    },
  };
  if (
    type === 'text' ||
    type === 'email' ||
    type === 'tel' ||
    type === 'password' ||
    type === 'search' ||
    type === 'url' ||
    type === 'number' ||
    type === 'date' ||
    type === 'datetime-local' ||
    type === 'time' ||
    type === 'month' ||
    type === 'week'
  ) {
    base.pause_before_focus_ms = null;
    base.focus_duration_ms = 0;
    base.focus_by_click_count = 0;
    base.focus_by_tab_count = 0;
    base.empty_focus_count = 0;
    base.pause_before_input_ms = null;
    base.value_length = 0;
    base.revisit_count = 0;
    base.revision_count = 0;
    base.reenter_count = 0;
    base.reenter_by_paste_count = 0;
    base.abandonment_and_return_count = 0;
    base.returned_to_original_count = 0;
    base.completion_time_ms = null;
    base.corrections = {
      count: 0,
      depth_avg: 0,
      backspace_count: 0,
      del_count: 0,
      replace_count: 0,
      _bursts: createWelford(),
      _latency: createWelford(),
    };
    base.paste_count = 0;
    base.edit_after_paste_count = 0;
    base.right_click_count = 0;
    base.autocomplete = { detected: false, edits_after: 0 };
    base.autocomplete_accepted_count = 0;
    base.keystroke = {
      total_count: 0,
      first_typing_at: 0,
      last_typing_at: 0,
      dwell: createWelford(),
      flight: createWelford(),
      overlap_count: 0,
      long_pause_count: 0,
    };
  } else if (type === 'file') {
    base.changes_count = 0;
    base.files_count_last = 0;
    base.files_count_total = 0;
    base.files_count_max = 0;
    base.multiple_enabled = false;
    base.multiple_selection_count = 0;
    base.cancel_count = 0;
    base._selection_latency = createWelford();
  } else if (type === 'range') {
    base.focus_duration_ms = 0;
    base.changes_count = 0;
    base.value_to_max_ratio = null;
    base.value_looks_rounded = null;
    base.max_amt_flag = null;
  } else {
    base.changes_count = 0;
  }
  if (type === 'number') {
    base.value_looks_rounded = null;
  }
  return base;
}

/**
 * Snapshot of state suitable for sessionStorage (Maps → plain objects, Welford serialized).
 */
export function snapshotForStorage(s) {
  return {
    lifecycle: s.lifecycle,
    startedAt: s.startedAt,
    endedAt: s.endedAt,
    pausedTotalMs: s.pausedTotalMs,
    pausedAt: s.pausedAt,
    session: serializeSession(s.session),
    pages: Array.from(s.pages.entries()).map(([k, v]) => [k, { ...v }]),
    currentPageKey: s.currentPageKey,
    currentPageEnteredAt: s.currentPageEnteredAt,
    sections: Array.from(s.sections.entries()).map(([k, v]) => [k, serializeSection(v)]),
    forms: Array.from(s.forms.entries()).map(([k, v]) => [k, serializeForm(v)]),
    fields: Array.from(s.fields.entries()).map(([k, v]) => [k, serializeField(v)]),
  };
}

/**
 * Inverse of snapshotForStorage.
 */
export function hydrateFromStorage(o) {
  if (!o || typeof o !== 'object') return createEmpty();
  const next = createEmpty();
  next.lifecycle = typeof o.lifecycle === 'string' ? o.lifecycle : LIFECYCLE_IDLE;
  next.startedAt = Number(o.startedAt) || 0;
  next.endedAt = Number(o.endedAt) || 0;
  next.pausedTotalMs = Number(o.pausedTotalMs) || 0;
  next.pausedAt = Number(o.pausedAt) || 0;
  if (o.session) next.session = deserializeSession(o.session);
  next.currentPageKey = typeof o.currentPageKey === 'string' ? o.currentPageKey : null;
  next.currentPageEnteredAt = Number(o.currentPageEnteredAt) || 0;
  if (Array.isArray(o.pages)) {
    for (const entry of o.pages) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [k, v] = entry;
      if (typeof k !== 'string' || !v) continue;
      const agg = createPageAgg();
      Object.assign(agg, v);
      next.pages.set(k, agg);
    }
  }
  if (Array.isArray(o.sections)) {
    for (const entry of o.sections) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [k, v] = entry;
      if (typeof k !== 'string' || !v) continue;
      next.sections.set(k, deserializeSection(v));
    }
  }
  if (Array.isArray(o.forms)) {
    for (const entry of o.forms) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [k, v] = entry;
      if (typeof k !== 'string' || !v) continue;
      next.forms.set(k, deserializeForm(v));
    }
  }
  if (Array.isArray(o.fields)) {
    for (const entry of o.fields) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [k, v] = entry;
      if (typeof k !== 'string' || !v) continue;
      next.fields.set(k, deserializeField(v));
    }
  }
  return next;
}

function serializePointerBucket(p) {
  return {
    sumDist: p.sumDist,
    sumTime: p.sumTime,
    sampleCount: p.sampleCount,
    speed: serializeWelford(p.speed),
    accel: serializeWelford(p.accel),
    rightClicks: p.rightClicks,
    clicks: { ...p.clicks },
    latency: serializeWelford(p.latency),
    accuracyPx: serializeWelford(p.accuracyPx),
    accuracyNorm: serializeWelford(p.accuracyNorm),
    pathDeviation: serializeWelford(p.pathDeviation),
    pressure: serializeWelford(p.pressure),
    width: serializeWelford(p.width),
    scroll: {
      count: p.scroll.count,
      sumDist: p.scroll.sumDist,
      sumTime: p.scroll.sumTime,
      speed: serializeWelford(p.scroll.speed),
      depthMax: p.scroll.depthMax,
    },
    trajectory: serializeTrajectory(p.trajectory),
  };
}

function deserializePointerBucket(o) {
  const p = createPointerBucket();
  if (!o) return p;
  if (Number.isFinite(o.sumDist)) p.sumDist = o.sumDist;
  if (Number.isFinite(o.sumTime)) p.sumTime = o.sumTime;
  if (Number.isFinite(o.sampleCount)) p.sampleCount = o.sampleCount;
  p.speed = deserializeWelford(o.speed);
  p.accel = deserializeWelford(o.accel);
  if (Number.isFinite(o.rightClicks)) p.rightClicks = o.rightClicks;
  if (o.clicks) {
    if (Number.isFinite(o.clicks.total)) p.clicks.total = o.clicks.total;
    if (Number.isFinite(o.clicks.onInteractive)) p.clicks.onInteractive = o.clicks.onInteractive;
  }
  p.latency = deserializeWelford(o.latency);
  p.accuracyPx = deserializeWelford(o.accuracyPx);
  p.accuracyNorm = deserializeWelford(o.accuracyNorm);
  p.pathDeviation = deserializeWelford(o.pathDeviation);
  p.pressure = deserializeWelford(o.pressure);
  p.width = deserializeWelford(o.width);
  if (o.scroll) {
    if (Number.isFinite(o.scroll.count)) p.scroll.count = o.scroll.count;
    if (Number.isFinite(o.scroll.sumDist)) p.scroll.sumDist = o.scroll.sumDist;
    if (Number.isFinite(o.scroll.sumTime)) p.scroll.sumTime = o.scroll.sumTime;
    p.scroll.speed = deserializeWelford(o.scroll.speed);
    if (Number.isFinite(o.scroll.depthMax)) p.scroll.depthMax = clampUnitInterval(o.scroll.depthMax);
  }
  p.trajectory = deserializeTrajectory(o.trajectory);
  return p;
}

function serializeSession(sess) {
  return {
    pageFocusCount: sess.pageFocusCount,
    orientationChangeCount: sess.orientationChangeCount,
    priorSessionCount: sess.priorSessionCount,
    activeDaysCount: sess.activeDaysCount,
    lastFieldChangeAt: sess.lastFieldChangeAt,
    pauseBeforeSubmitMs: sess.pauseBeforeSubmitMs,
    pointer: serializePointerBucket(sess.pointer),
    touch: serializePointerBucket(sess.touch),
  };
}

function deserializeSession(o) {
  const sess = createSessionAccumulators();
  if (!o) return sess;
  if (Number.isFinite(o.pageFocusCount)) sess.pageFocusCount = o.pageFocusCount;
  if (Number.isFinite(o.orientationChangeCount)) sess.orientationChangeCount = o.orientationChangeCount;
  if (Number.isFinite(o.priorSessionCount)) sess.priorSessionCount = o.priorSessionCount;
  if (Number.isFinite(o.activeDaysCount)) sess.activeDaysCount = o.activeDaysCount;
  if (Number.isFinite(o.lastFieldChangeAt)) sess.lastFieldChangeAt = o.lastFieldChangeAt;
  if (Number.isFinite(o.pauseBeforeSubmitMs)) sess.pauseBeforeSubmitMs = o.pauseBeforeSubmitMs;
  if (o.pointer) sess.pointer = deserializePointerBucket(o.pointer);
  if (o.touch) sess.touch = deserializePointerBucket(o.touch);
  return sess;
}

function serializeSection(sec) {
  return {
    pageKey: sec.pageKey,
    sectionId: sec.sectionId,
    section_categories: sec.section_categories.slice(),
    text_length: sec.text_length,
    visits_count: sec.visits_count,
    visits: sec.visits.slice(),
    reading_time_ms: sec.reading_time_ms,
    pause_count: sec.pause_count,
    time_total_ms: sec.time_total_ms,
    scroll: {
      count: sec.scroll.count,
      sumDist: sec.scroll.sumDist,
      sumTime: sec.scroll.sumTime,
      speed: serializeWelford(sec.scroll.speed),
      depthMax: sec.scroll.depthMax,
    },
    zoom_count: sec.zoom_count,
    selection_count: sec.selection_count,
  };
}

function deserializeSection(o) {
  const sec = createSectionAgg();
  sec.pageKey = typeof o.pageKey === 'string' ? o.pageKey : null;
  sec.sectionId = typeof o.sectionId === 'string' ? o.sectionId : null;
  if (Number.isFinite(o.text_length)) sec.text_length = o.text_length;
  if (Number.isFinite(o.visits_count)) sec.visits_count = o.visits_count;
  if (Array.isArray(o.visits)) sec.visits = o.visits.filter(Number.isFinite);
  if (Number.isFinite(o.reading_time_ms)) sec.reading_time_ms = o.reading_time_ms;
  if (Number.isFinite(o.pause_count)) sec.pause_count = o.pause_count;
  if (Number.isFinite(o.time_total_ms)) sec.time_total_ms = o.time_total_ms;
  if (Array.isArray(o.section_categories))
    sec.section_categories = o.section_categories.filter((value) => typeof value === 'string');
  if (o.scroll) {
    if (Number.isFinite(o.scroll.count)) sec.scroll.count = o.scroll.count;
    if (Number.isFinite(o.scroll.sumDist)) sec.scroll.sumDist = o.scroll.sumDist;
    if (Number.isFinite(o.scroll.sumTime)) sec.scroll.sumTime = o.scroll.sumTime;
    sec.scroll.speed = deserializeWelford(o.scroll.speed);
    if (Number.isFinite(o.scroll.depthMax)) sec.scroll.depthMax = clampUnitInterval(o.scroll.depthMax);
  }
  if (Number.isFinite(o.zoom_count)) sec.zoom_count = o.zoom_count;
  if (Number.isFinite(o.selection_count)) sec.selection_count = o.selection_count;
  return sec;
}

function serializeForm(f) {
  return {
    pageKey: f.pageKey,
    formId: f.formId,
    is_submitted: f.is_submitted,
    submission_time_ms: f.submission_time_ms,
    time_on_form_ms: f.time_on_form_ms,
    active_time_on_form_ms: f.active_time_on_form_ms,
    visit_count_session: f.visit_count_session,
    visit_count_lifetime: f.visit_count_lifetime,
    visits_before_submission_count: f.visits_before_submission_count,
    corrections: { ...f.corrections },
    runtime: {
      firstVisibleAt: f.runtime.firstVisibleAt,
      firstFocusAt: f.runtime.firstFocusAt,
      lastFocusOutAt: f.runtime.lastFocusOutAt,
      visitsSeen: f.runtime.visitsSeen,
      lastVisitMarkAt: f.runtime.lastVisitMarkAt,
      lastInputFieldName: f.runtime.lastInputFieldName,
    },
    fields: Array.from(f.fields.keys()),
  };
}

function deserializeForm(o) {
  const f = createFormAgg();
  f.pageKey = o.pageKey;
  f.formId = o.formId;
  f.is_submitted = !!o.is_submitted;
  f.submission_time_ms = Number.isFinite(o.submission_time_ms) ? o.submission_time_ms : null;
  if (Number.isFinite(o.time_on_form_ms)) f.time_on_form_ms = o.time_on_form_ms;
  if (Number.isFinite(o.active_time_on_form_ms)) f.active_time_on_form_ms = o.active_time_on_form_ms;
  if (Number.isFinite(o.visit_count_session)) f.visit_count_session = o.visit_count_session;
  if (Number.isFinite(o.visit_count_lifetime)) f.visit_count_lifetime = o.visit_count_lifetime;
  f.visits_before_submission_count = Number.isFinite(o.visits_before_submission_count)
    ? o.visits_before_submission_count
    : null;
  if (o.corrections) {
    if (Number.isFinite(o.corrections.early_count)) f.corrections.early_count = o.corrections.early_count;
    if (Number.isFinite(o.corrections.late_count)) f.corrections.late_count = o.corrections.late_count;
    if (Number.isFinite(o.corrections.total_count)) f.corrections.total_count = o.corrections.total_count;
    f.corrections.late_ratio = Number.isFinite(o.corrections.late_ratio) ? o.corrections.late_ratio : null;
  }
  if (o.runtime) {
    if (Number.isFinite(o.runtime.firstVisibleAt)) f.runtime.firstVisibleAt = o.runtime.firstVisibleAt;
    if (Number.isFinite(o.runtime.firstFocusAt)) f.runtime.firstFocusAt = o.runtime.firstFocusAt;
    if (Number.isFinite(o.runtime.lastFocusOutAt)) f.runtime.lastFocusOutAt = o.runtime.lastFocusOutAt;
    if (Number.isFinite(o.runtime.visitsSeen)) f.runtime.visitsSeen = o.runtime.visitsSeen;
    if (Number.isFinite(o.runtime.lastVisitMarkAt)) f.runtime.lastVisitMarkAt = o.runtime.lastVisitMarkAt;
    if (typeof o.runtime.lastInputFieldName === 'string') f.runtime.lastInputFieldName = o.runtime.lastInputFieldName;
  }
  return f;
}

function serializeField(fld) {
  const out = { name: fld.name, type: fld.type };
  if (fld.autocomplete && fld.runtime) {
    out.runtime = {
      autocompleteAcceptedRecorded: !!fld.runtime.autocompleteAcceptedRecorded,
      autocompleteDetectedThisFocus: !!fld.runtime.autocompleteDetectedThisFocus,
    };
  }
  for (const k of Object.keys(fld)) {
    if (k === 'runtime' || k === 'name' || k === 'type') continue;
    out[k] = serializeFieldBranch(fld[k]);
  }
  return out;
}

function serializeFieldBranch(v) {
  if (v == null) return v;
  if (typeof v !== 'object') return v;
  if (v && typeof v.n === 'number' && typeof v.mean === 'number' && typeof v.m2 === 'number') {
    return serializeWelford(v);
  }
  if (Array.isArray(v)) return v.slice();
  const out = {};
  for (const k of Object.keys(v)) out[k] = serializeFieldBranch(v[k]);
  return out;
}

function deserializeField(o) {
  const fld = createFieldAgg(o.name, o.type);
  for (const k of Object.keys(o)) {
    // `change_count` was the previous choice-control counter. Do not restore it:
    // new sessions use `changes_count` and old snapshots intentionally start it at 0.
    if (k === 'name' || k === 'type' || k === 'runtime' || k === 'change_count') continue;
    fld[k] = restoreFieldBranch(fld[k], o[k]);
  }
  if (fld.autocomplete) delete fld.autocomplete.available;
  if (o.runtime && fld.autocomplete) {
    // The accepted DOM value is intentionally held in a WeakMap and is not
    // persisted. Keep the per-focus acceptance guard, however, so replayed
    // browser events after bb pause/resume cannot count the same acceptance.
    fld.runtime.autocompleteAcceptedRecorded = !!o.runtime.autocompleteAcceptedRecorded;
    fld.runtime.autocompleteDetectedThisFocus = false;
  }
  return fld;
}

function restoreFieldBranch(template, value) {
  if (value == null) return value;
  if (typeof value !== 'object') return value;
  if (template && typeof template === 'object' && 'n' in template && 'mean' in template) {
    return deserializeWelford(value);
  }
  if (Array.isArray(value)) return value.slice();
  const out = template && typeof template === 'object' ? { ...template } : {};
  for (const k of Object.keys(value)) {
    out[k] = restoreFieldBranch(template && template[k], value[k]);
  }
  return out;
}
